/**
 * ARTICLE D'EXEMPLE — généré le 2026-09-06 pour valider la maquette du blog.
 * À relire, compléter ou remplacer par du contenu réel avant/après mise en ligne.
 * Sources : 6 URL vérifiées automatiquement (6 OK).
 * Format : voir src/blog/README.md
 */
export default {
  "slug": "se-former-ia-besancon-guide-voies-acces",
  "title": "Se former à l’IA à Besançon : le guide des voies d’accès, du bac au master",
  "excerpt": "Licence et master informatique, écoles d’ingénieurs, titre professionnel du GRETA-CFA, MOOC : tour d’horizon des parcours pour apprendre l’IA et le numérique à Besançon.",
  "seoTitle": "Se former à l’IA : université, écoles, GRETA",
  "seoDescription": "Licence et master informatique, SUPMICROTECH-ENSMM, ISIFC, titre professionnel du GRETA-CFA en alternance, MOOC : les parcours pour se former à l’IA à Besançon.",
  "category": "formation",
  "tags": [
    "Formation",
    "GRETA-CFA",
    "Université Marie et Louis Pasteur",
    "SUPMICROTECH-ENSMM",
    "Reconversion"
  ],
  "author": "jimmy",
  "publishedAt": "2026-08-14",
  "updatedAt": "2026-08-14",
  "featured": false,
  "cover": {
    "image": "/img/blog/unsplash-1522202176988-66273c2fd55f.jpg",
    "credit": "Photo : Unsplash",
    "alt": "Étudiants travaillant sur des ordinateurs portables dans une salle de cours lumineuse d’un campus universitaire à Besançon"
  },
  "content": [
    {
      "type": "lead",
      "text": "Apprendre à programmer, comprendre le machine learning ou simplement acquérir une culture solide du numérique : à Besançon, les voies sont plus nombreuses qu’on ne le croit, de la licence universitaire au titre professionnel en alternance. Ce guide passe en revue les principales portes d’entrée, selon votre niveau de départ et votre projet, reconversion comprise."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Université",
          "value": "Licence et master Informatique à l’UFR Sciences et Techniques (Université Marie et Louis Pasteur)"
        },
        {
          "label": "Écoles d’ingénieurs",
          "value": "SUPMICROTECH-ENSMM (mécanique, mécatronique, microsystèmes) et ISIFC (génie biomédical)"
        },
        {
          "label": "Reconversion",
          "value": "Titre « Développeur en intelligence artificielle » (niveau 6) au GRETA-CFA, en alternance"
        },
        {
          "label": "Recherche",
          "value": "Département informatique (DISC) de l’institut FEMTO-ST, adossé au master"
        },
        {
          "label": "En ligne",
          "value": "MOOC universitaires (FUN MOOC) en complément d’un parcours diplômant"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Un paysage universitaire recomposé"
    },
    {
      "type": "p",
      "text": "Premier repère, administratif mais utile : l’Université de Franche-Comté n’existe plus sous ce nom. Depuis le 1er janvier 2025, elle est intégrée à l’[Université Marie et Louis Pasteur](https://www.umlp.fr/) (UMLP), un établissement public expérimental dont le siège est à Besançon et qui compte également SUPMICROTECH-ENSMM et l’UTBM parmi ses établissements-composantes.[^2] Cela ne change rien aux diplômes délivrés, mais les sites web, les brochures et les fiches Parcoursup portent désormais ce nouveau nom, ce qui peut dérouter au moment de chercher une formation."
    },
    {
      "type": "p",
      "text": "À Besançon, les formations en informatique de l’université sont portées par l’UFR Sciences et Techniques, sur le campus de la Bouloie. Elles s’adossent au département informatique (DISC) de [l’institut FEMTO-ST](/blog/femto-st-besancon-capitale-microtechniques), l’un des grands laboratoires de recherche de la région, dont les équipes irriguent directement les enseignements de master.[^3]"
    },
    {
      "type": "h2",
      "text": "Du bac au master : la voie universitaire"
    },
    {
      "type": "p",
      "text": "Après le bac, la porte d’entrée classique est la licence mention Informatique de l’UFR Sciences et Techniques, accessible via Parcoursup. L’UMLP propose aussi un Cursus Master en Ingénierie (CMI) en informatique : un parcours renforcé sur cinq ans, avec davantage de projets et d’ouverture à la recherche, destiné aux bacheliers dont le projet est déjà affirmé.[^2]"
    },
    {
      "type": "p",
      "text": "En master, la mention Informatique de Besançon compte quatre parcours. Le plus directement tourné vers le développement, « Ingénierie Systèmes et Logiciels » (ISL), peut se suivre en alternance et vise les métiers du développement logiciel ; ses enseignements s’appuient sur les thématiques des équipes de recherche du DISC, dont l’une travaille notamment sur le calcul haute performance et l’intelligence artificielle distribuée.[^3] Un parcours international, en anglais et orienté recherche, est proposé via l’école universitaire de recherche EIPHI.[^3]"
    },
    {
      "type": "p",
      "text": "Un conseil au passage : ne cherchez pas à tout prix un diplôme avec « IA » dans l’intitulé. À l’université, l’intelligence artificielle s’aborde d’abord par l’informatique générale — algorithmique, systèmes, données —, ce qui constitue, pour beaucoup de recruteurs, une base plus durable qu’une spécialisation trop précoce."
    },
    {
      "type": "h2",
      "text": "Les écoles d’ingénieurs bisontines"
    },
    {
      "type": "p",
      "text": "[SUPMICROTECH-ENSMM](https://www.supmicrotech.fr/), l’École nationale supérieure de mécanique et des microtechniques, forme des ingénieurs à la croisée de la mécanique, de la mécatronique et des microsystèmes. Elle recrute principalement après une classe préparatoire, via le concours commun CCINP, et délivre son diplôme en formation initiale, par apprentissage ou en formation continue ; elle héberge par ailleurs trois départements de recherche de FEMTO-ST.[^4] Ce n’est pas une école d’informatique, mais c’est la voie naturelle pour qui veut travailler sur l’IA embarquée, la robotique ou l’instrumentation."
    },
    {
      "type": "p",
      "text": "L’[ISIFC](https://isifc.univ-fcomte.fr/), école d’ingénieurs interne à l’université, est spécialisée dans le génie biomédical et le dispositif médical. Elle recrute à partir d’un bac+2 scientifique pour trois années d’études, avec des options de spécialisation en troisième année.[^5] Pour [l’IA appliquée à la santé](/blog/besancon-terrain-naturel-ia-sante-chu-isifc-temis) — imagerie, capteurs, données cliniques —, c’est un point d’entrée singulier dans le paysage local."
    },
    {
      "type": "h2",
      "text": "Reconversion : le titre « Développeur en intelligence artificielle » du GRETA-CFA"
    },
    {
      "type": "p",
      "text": "Pour les adultes en reconversion ou les développeurs qui souhaitent se spécialiser, le [GRETA-CFA Franche-Comté](https://greta-franche-comte.fr/formation/developpeur-intelligence-artificielle/) propose à Besançon une formation menant au titre professionnel « Développeur en intelligence artificielle », de niveau 6 (équivalent bac+3), enregistré au RNCP sous le numéro 37827 et certifié par Simplon.co.[^1] Elle se déroule au lycée Jules Haag, en alternance."
    },
    {
      "type": "ul",
      "items": [
        "Durée : 1 820 heures, dont 455 heures en centre de formation et 1 365 heures en entreprise.[^1]",
        "Prérequis : un niveau solide en programmation, la maîtrise de Python étant exigée, environ une année de formation au développement et une première expérience professionnelle en informatique, ainsi que des notions sur les modèles d’IA (traitement du langage, vision, séries temporelles).[^1]",
        "Contenus : apprentissage par renforcement, déploiement continu, API, supervision de modèles, traitement de données et Big Data, puis un projet certifiant couvrant toute la chaîne, de la donnée au modèle intégré.[^1]",
        "Accès : contrat d’apprentissage ou de professionnalisation, ou financement individuel, notamment via le CPF.[^1]",
        "Débouchés : développeur d’applications IA, développeur junior en IA, profils « ops » chargés du déploiement.[^1]"
      ]
    },
    {
      "type": "p",
      "text": "Autrement dit, ce n’est pas une formation pour grands débutants : il faut déjà savoir coder. Pour partir de zéro, les structures académiques de formation continue et d’apprentissage (GRETA-CFA, CFA académique) proposent aussi des BTS du numérique, comme le BTS Services informatiques aux organisations, en apprentissage à Belfort ou à Besançon (lycée Louis Pergaud), qui peuvent servir de marchepied vers ce titre ou vers l’université."
    },
    {
      "type": "h3",
      "text": "Et en ligne ?"
    },
    {
      "type": "p",
      "text": "Les MOOC restent un bon complément, en particulier pour tester son appétence avant de s’engager. La plateforme publique [FUN MOOC](https://www.fun-mooc.fr/) (France Université Numérique) réunit des cours gratuits ou payants proposés par des universités et des grandes écoles, assortis pour certains de micro-certifications.[^6] Ils ne remplacent pas un diplôme, mais permettent d’arriver en formation avec les bases de Python ou de statistiques déjà en place."
    },
    {
      "type": "callout",
      "title": "Pour bien choisir",
      "tone": "tip",
      "text": "Partez de votre niveau réel plutôt que du diplôme visé : sans bases de programmation, commencez par une licence, un BTS ou quelques MOOC avant de viser le titre du GRETA-CFA ou un master. Si vous êtes salarié, renseignez-vous sur le compte personnel de formation (CPF) et sur les contrats en alternance, qui financent l’essentiel des parcours décrits ici. Et dans tous les cas, allez aux journées portes ouvertes : à Besançon, les établissements sont à quelques kilomètres les uns des autres."
    },
    {
      "type": "h2",
      "text": "Une offre complète, à condition de bien s’orienter"
    },
    {
      "type": "p",
      "text": "Besançon n’a pas de grande école dédiée à l’IA, et ce n’est pas nécessairement un manque : la ville aligne une filière universitaire solide adossée à un laboratoire de premier plan, deux écoles d’ingénieurs aux spécialités bien identifiées et une voie de reconversion en alternance conçue pour des profils déjà techniques. L’enjeu, pour les candidats, est surtout de lire correctement les prérequis ; pour l’écosystème local, il est de continuer à faire dialoguer ces formations avec les entreprises qui recrutent."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Développeur en intelligence artificielle – En apprentissage",
      "publisher": "GRETA-CFA Franche-Comté",
      "url": "https://greta-franche-comte.fr/formation/developpeur-intelligence-artificielle/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 2,
      "title": "Université Marie et Louis Pasteur – Site officiel",
      "publisher": "Université Marie et Louis Pasteur",
      "url": "https://www.umlp.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 3,
      "title": "Master Informatique – Département Informatique, UFR Sciences et Techniques",
      "publisher": "Université Marie et Louis Pasteur",
      "url": "https://dept-info.univ-fcomte.fr/master",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 4,
      "title": "SUPMICROTECH-ENSMM – École nationale supérieure de mécanique et des microtechniques",
      "publisher": "SupMicroTech-ENSMM",
      "url": "https://www.supmicrotech.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 5,
      "title": "ISIFC Génie Biomédical – Formation d’ingénieur",
      "publisher": "ISIFC / Université Marie et Louis Pasteur",
      "url": "https://isifc.univ-fcomte.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 6,
      "title": "FUN MOOC – France Université Numérique",
      "publisher": "France Université Numérique",
      "url": "https://www.fun-mooc.fr/",
      "type": "official",
      "date": "2026-09-06"
    }
  ]
};
